<template>
	<Teleport to="body">
		<div class="modal" :class="{ 'is-active': show }">
			<div class="modal-background" @click="close"></div>
			<div class="modal-card">
				<header class="modal-card-head">
					<p class="modal-card-title">{{ editingItem ? 'Edit ' + itemLabel : 'Add new ' + itemLabel }}</p>
					<button class="delete" aria-label="close" @click="close"></button>
				</header>
				<section class="modal-card-body">
					<div class="field" v-if="withImage">
						<label class="label">Image</label>
						<div
							class="dropzone"
							:class="{ 'is-dragover': isDragOver }"
							@dragover.prevent="isDragOver = true"
							@dragleave.prevent="isDragOver = false"
							@drop.prevent="onDrop"
							@click="$refs.fileInput.click()"
						>
							<img :src="previewImg" alt="Preview" class="dropzone-preview" />
							<p class="dropzone-hint">Click or drop an image here</p>
						</div>
						<input ref="fileInput" type="file" accept="image/jpeg,image/png,image/webp" class="is-hidden" @change="onFileChange" />
						<p class="help">JPG, PNG or WebP, max 5MB, 1024x512px.</p>
						<p class="help is-danger" v-if="imgError">{{ imgError }}</p>
					</div>
					<div class="field">
						<label class="label">Title</label>
						<div class="control">
							<input class="input" type="text" v-model="title" @keyup.enter="submit" />
						</div>
					</div>
					<div class="field">
						<label class="label">Author</label>
						<div class="control">
							<input class="input" type="text" v-model="author" @keyup.enter="submit" />
						</div>
					</div>
					<div class="field">
						<label class="label">Description</label>
						<div class="control">
							<textarea class="textarea" v-model="description" rows="2"></textarea>
						</div>
					</div>
					<div class="field">
						<label class="label">Download</label>
						<div class="buttons has-addons mb-2">
							<button
								type="button"
								class="button is-small"
								:class="{ 'is-selected is-success': downloadMode === 'link' }"
								@click="downloadMode = 'link'"
							>
								Link (URL)
							</button>
							<button
								type="button"
								class="button is-small"
								:class="{ 'is-selected is-success': downloadMode === 'file' }"
								@click="downloadMode = 'file'"
							>
								Upload file
							</button>
						</div>
						<div v-if="downloadMode === 'link'" class="control">
							<input class="input" type="url" v-model="downloadLinkUrl" placeholder="https://..." @keyup.enter="submit" />
						</div>
						<div v-else>
							<div
								class="dropzone"
								:class="{ 'is-dragover': isFileDragOver }"
								@dragover.prevent="isFileDragOver = true"
								@dragleave.prevent="isFileDragOver = false"
								@drop.prevent="onZipDrop"
								@click="$refs.zipInput.click()"
							>
								<p class="dropzone-hint">{{ zipHint }}</p>
							</div>
							<input ref="zipInput" type="file" :accept="acceptAttr" class="is-hidden" @change="onZipChange" />
						</div>
						<p class="help is-danger" v-if="downloadError">{{ downloadError }}</p>
					</div>
					<p class="help is-danger" v-if="error">Please enter a title.</p>
					<p class="help is-danger" v-if="submitError">{{ submitError }}</p>
				</section>
				<footer class="modal-card-foot">
					<button class="button is-success" :class="{ 'is-loading': isSubmitting }" :disabled="isSubmitting" @click="submit">
						{{ editingItem ? 'Save' : 'Add' }}
					</button>
					<button class="button" @click="close">Cancel</button>
				</footer>
			</div>
		</div>
	</Teleport>
</template>

<script>
import NoImage from '@/assets/images/NoImage.jpg';
import auth from '@/auth.js';

const MAX_ZIP_SIZE_DEFAULT = 15 * 1024 * 1024;
const MAX_ZIP_SIZE_ADMIN = 250 * 1024 * 1024;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const MAX_IMAGE_WIDTH = 1024;
const MAX_IMAGE_HEIGHT = 512;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default {
	name: 'AddItemModal',
	props: {
		show: Boolean,
		editingItem: { type: Object, default: null },
		withImage: { type: Boolean, default: true },
		itemLabel: { type: String, default: 'item' },
		allowedExtensions: { type: Array, default: () => ['.zip'] },
		onSubmit: { type: Function, required: true },
	},
	emits: ['close'],
	data() {
		return {
			title: '',
			author: '',
			description: '',
			imgData: null,
			isDragOver: false,
			imgError: '',
			error: false,
			downloadMode: 'link',
			downloadLinkUrl: '',
			downloadFile: null,
			isFileDragOver: false,
			downloadError: '',
			isSubmitting: false,
			submitError: '',
		};
	},
	computed: {
		maxZipSize() {
			return auth.state.username === 'Admin' ? MAX_ZIP_SIZE_ADMIN : MAX_ZIP_SIZE_DEFAULT;
		},
		previewImg() {
			return this.imgData || (this.editingItem ? this.editingItem.img : '') || NoImage;
		},
		zipHint() {
			if (this.downloadFile) return this.downloadFile.name;
			if (this.editingItem && this.editingItem.downloadFilename) {
				return `Current: ${this.editingItem.downloadFilename} (click to replace)`;
			}
			return `Click or drop a ${this.allowedExtensions.join(' or ')} file here`;
		},
		acceptAttr() {
			return this.allowedExtensions.join(',');
		},
	},
	watch: {
		show(val) {
			if (val) this.populate();
		},
	},
	methods: {
		populate() {
			this.reset();
			if (this.editingItem) {
				this.title = this.editingItem.title || '';
				this.author = this.editingItem.author || '';
				this.description = this.editingItem.desc || '';
				if (this.editingItem.downloadFilename) {
					this.downloadMode = 'file';
				} else {
					this.downloadMode = 'link';
					this.downloadLinkUrl = this.editingItem.downloadLink || '';
				}
			}
		},
		onFileChange(e) {
			const file = e.target.files[0];
			if (file) this.readFile(file);
		},
		onDrop(e) {
			this.isDragOver = false;
			const file = e.dataTransfer.files[0];
			if (file) this.readFile(file);
		},
		readFile(file) {
			this.imgError = '';
			if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
				this.imgError = 'Please use a JPG, PNG or WebP image.';
				return;
			}
			if (file.size > MAX_IMAGE_SIZE) {
				this.imgError = 'Image exceeds the 5MB limit.';
				return;
			}
			const reader = new FileReader();
			reader.onload = () => {
				const img = new Image();
				img.onload = () => {
					if (img.width > MAX_IMAGE_WIDTH || img.height > MAX_IMAGE_HEIGHT) {
						this.imgError = `Image dimensions must be at most ${MAX_IMAGE_WIDTH}x${MAX_IMAGE_HEIGHT}px.`;
						return;
					}
					this.imgData = reader.result;
				};
				img.src = reader.result;
			};
			reader.readAsDataURL(file);
		},
		onZipChange(e) {
			const file = e.target.files[0];
			if (file) this.readZip(file);
		},
		onZipDrop(e) {
			this.isFileDragOver = false;
			const file = e.dataTransfer.files[0];
			if (file) this.readZip(file);
		},
		readZip(file) {
			this.downloadError = '';
			const lowerName = file.name.toLowerCase();
			if (!this.allowedExtensions.some((ext) => lowerName.endsWith(ext))) {
				this.downloadError = `Please select a ${this.allowedExtensions.join(' or ')} file.`;
				return;
			}
			if (file.size > this.maxZipSize) {
				this.downloadError = `File exceeds the ${Math.round(this.maxZipSize / 1024 / 1024)}MB limit. Use a URL instead.`;
				return;
			}
			const reader = new FileReader();
			reader.onload = () => {
				this.downloadFile = { name: file.name, data: reader.result };
			};
			reader.readAsDataURL(file);
		},
		async submit() {
			if (!this.title.trim()) {
				this.error = true;
				return;
			}
			this.error = false;
			this.submitError = '';
			const payload = {
				img: this.withImage ? this.imgData || (this.editingItem ? this.editingItem.img : '') || NoImage : '',
				title: this.title.trim(),
				author: this.author.trim(),
				desc: this.description.trim(),
				downloadMode: this.downloadMode,
				downloadLink: this.downloadMode === 'link' ? this.downloadLinkUrl.trim() : '',
				downloadFile: this.downloadMode === 'file' ? this.downloadFile : null,
			};
			this.isSubmitting = true;
			const result = await this.onSubmit({
				isEdit: !!this.editingItem,
				id: this.editingItem ? this.editingItem.id : null,
				payload,
			});
			this.isSubmitting = false;
			if (result && result.ok) {
				this.reset();
				this.$emit('close');
			} else {
				this.submitError = (result && result.error) || 'Could not save this item.';
			}
		},
		close() {
			this.reset();
			this.$emit('close');
		},
		reset() {
			this.title = '';
			this.author = '';
			this.description = '';
			this.imgData = null;
			this.imgError = '';
			this.error = false;
			this.downloadMode = 'link';
			this.downloadLinkUrl = '';
			this.downloadFile = null;
			this.downloadError = '';
			this.isSubmitting = false;
			this.submitError = '';
		},
	},
};
</script>

<style scoped>
.modal {
	z-index: 1000;
}
.modal-card {
	width: 420px;
	max-width: 90vw;
}
.modal-card-head,
.modal-card-body,
.modal-card-foot {
	background-color: #1e1f25;
	color: white;
}
.modal-card-body {
	scrollbar-width: thin;
	scrollbar-color: #70cb7c #1e1f25;
}
.modal-card-body::-webkit-scrollbar {
	width: 8px;
}
.modal-card-body::-webkit-scrollbar-track {
	background: #1e1f25;
}
.modal-card-body::-webkit-scrollbar-thumb {
	background-color: #70cb7c;
	border-radius: 4px;
}
.modal-card-body::-webkit-scrollbar-thumb:hover {
	background-color: #89ce92;
}
.modal-card-title,
.label {
	color: white;
}
.input,
.textarea {
	background-color: #131417;
	border-color: #3a3c46;
	color: white;
}
.input::placeholder,
.textarea::placeholder {
	color: #6f7280;
}
.input:focus,
.textarea:focus {
	background-color: #131417;
	border-color: #70cb7c;
	color: white;
}
.dropzone {
	border: 2px dashed #70cb7c;
	cursor: pointer;
	padding: 24px 16px;
	min-height: 80px;
	display: flex;
	align-items: center;
	justify-content: center;
	text-align: center;
}
.dropzone.is-dragover {
	background-color: #282a32;
}
.field:has(.dropzone-preview) .dropzone {
	position: relative;
	overflow: hidden;
	aspect-ratio: 16 / 9;
	padding: 0;
	min-height: 0;
	display: block;
}
.dropzone-preview {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
}
.dropzone-hint {
	font-size: 0.8rem;
	color: #c7c9d2;
}
.field:has(.dropzone-preview) .dropzone-hint {
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	margin: 0;
	padding: 6px;
	color: white;
	background: linear-gradient(to top, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0));
}
.is-hidden {
	display: none;
}
.button.is-success {
	background-color: #70cb7c;
	border-color: #70cb7c;
	color: #131417;
}
.button.is-success:hover {
	background-color: #89ce92;
}
.buttons.has-addons .button:not(.is-success) {
	background-color: #282a32;
	border-color: #3a3c46;
	color: #c7c9d2;
}
</style>
